'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import Button from '@/components/common/Button';
import Chevron from '../../../public/assets/icons/chevron-right.svg';
import { heroSlides } from './heroSlides';

/** How long each slide stays on screen before auto-advancing (ms). */
const SLIDE_DURATION = 6000;

const HeroCarousel = () => {
  const count = heroSlides.length;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const goTo = useCallback(
    (index: number) => setActive(((index % count) + count) % count),
    [count]
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  // Respect the user's motion preference — no autoplay when reduced motion is on.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Pause autoplay when the tab is not visible.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const autoplay = !reducedMotion && count > 1;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    }
  };

  return (
    <section
      className="w-full"
      role="region"
      aria-roledescription="carousel"
      aria-label="Highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div className="relative grid overflow-hidden">
        {heroSlides.map((slide, i) => {
          const isActive = i === active;
          return (
            <div
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={!isActive}
              // Keep off-screen slides out of the tab order and the a11y tree
              // so the hidden CTAs aren't focusable during the transition.
              {...(!isActive ? ({ inert: '' } as object) : {})}
              className={classNames(
                'col-start-1 row-start-1',
                !isActive && 'pointer-events-none'
              )}
            >
              {/* Background: crossfade + slow ease-out zoom as the slide enters. */}
              <div
                className={classNames(
                  'absolute inset-0 overflow-hidden transition-opacity duration-700 ease-out',
                  isActive ? 'opacity-100' : 'opacity-0'
                )}
              >
                <Image
                  src={slide.image}
                  alt={slide.imageAlt}
                  quality={100}
                  fill
                  className={classNames(
                    'object-cover transition-transform duration-[1400ms] ease-out',
                    isActive ? 'scale-100' : 'scale-105'
                  )}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  priority={i === 0}
                  sizes="(min-width: 1780px) 1584px, (min-width: 1440px) calc(45vw + 792px), 1344px"
                  unoptimized
                />
                <div className="home-header-gradient" />
                <div className="home-header-bottom-gradient" />
              </div>
              {/* Content: slides up from below with an ease-out, slightly after
                  the background so the motion reads as one smooth entrance. */}
              <div
                className={classNames(
                  'relative flex min-h-[43.75rem] flex-col px-4 pt-25 transition-all ease-out sm:px-10',
                  isActive
                    ? 'translate-y-0 opacity-100 delay-150 duration-700'
                    : 'translate-y-8 opacity-0 duration-500'
                )}
              >
                <p className="square-subtitle mb-4 text-white">
                  {slide.eyebrow}
                </p>
                <h1 className="text-h4 sm:text-h3 lg:text-h2 mb-[20px] font-light text-white">
                  {slide.title}
                </h1>
                <p className="text-lg sm:text-xl lg:text-h6 mt-1 max-w-xl font-[400] text-white">
                  {slide.description}
                </p>
                <Button
                  href={slide.ctaHref}
                  className="mt-8 w-72 max-w-full justify-center font-bold !bg-white !text-primary-10 hover:!bg-white/90"
                >
                  {slide.ctaLabel}
                  <span className="pl-2">
                    <Chevron />
                  </span>
                </Button>
              </div>
            </div>
          );
        })}

        {/* Right-side indicators: dots, active one is a bar that fills. */}
        <div className="absolute right-4 top-1/2 z-20 flex -translate-y-1/2 flex-col items-center gap-3 sm:right-10">
          {heroSlides.map((slide, i) => {
            const isActive = i === active;
            return (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}: ${slide.eyebrow}`}
                aria-current={isActive}
                className="group flex items-center justify-center p-1.5"
              >
                <span
                  className={classNames(
                    'relative block overflow-hidden rounded-full transition-all duration-300',
                    isActive
                      ? 'h-7 w-2.5 bg-white/30'
                      : 'h-2.5 w-2.5 bg-white/40 group-hover:bg-white/80'
                  )}
                >
                  {isActive &&
                    (autoplay ? (
                      // CSS-driven fill: scaleY on the compositor, buttery smooth.
                      // Its animationend is what advances the carousel, so the
                      // fill and the slide change are always perfectly in sync.
                      <span
                        className="hero-indicator-fill absolute inset-0 rounded-full bg-white"
                        style={{
                          animationDuration: `${SLIDE_DURATION}ms`,
                          animationPlayState: paused ? 'paused' : 'running',
                        }}
                        onAnimationEnd={next}
                      />
                    ) : (
                      <span className="absolute inset-0 rounded-full bg-white" />
                    ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
